import { defineComponent as _defineComponent } from 'vue'
import router from "@/router";
  import store from "@/store";
  import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
  import {
    computed,
    ref,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
    reactive,
  } from "vue";
  import { CustomizeHouseStepsEnum } from "@/enums/CustomizeHouseSteps";
  import { UserQuoteSelectionInterface } from "@/types/UserQuoteSelectionInterface";

  import { LotActionInterface } from "@/types/LotActionInterface";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { useToast, POSITION } from "vue-toastification";
  import InitialHoldModal from "@/components/ui/modals/InitialHoldModal.vue";
  import InitialHoldWithPartnerModal from "@/components/ui/modals/InitialHoldWithPartnerModal.vue";
  import SuccessInitialHoldModal from "@/components/ui/modals/SuccessInitialHoldModal.vue";
  import BuilderRequestQuoteModal from "@/components/ui/modals/BuilderRequestQuoteModal.vue";
  import { redirectIfBuilderIsDisabled } from "@/helpers/builder/redirectIfBuilderIsDisabled";
  import { changePrimaryColor } from "@/helpers/customer/changePrimaryColor";
  import BuilderHeader from "@/components/builder/BuilderHeader.vue";
  import { EstateInterface } from "@/types/EstateInterface";
  import BuilderQuotesListsModal from "@/components/ui/modals/BuilderQuotesListsModal.vue";
  import BuilderQuotesLists from "@/components/builder/BuilderQuotesLists.vue";
  import BuilderSelectionHouse from "@/components/builder/BuilderSelectionHouse.vue";
  import { userSelectionDefault } from "@/constants/BuilderUserSelectionDefault";
  import BuilderSelectionFacades from "@/components/builder/BuilderSelectionFacades.vue";
  import {
    scrollToNextStep,
    scrollToNextStepMobile,
  } from "@/helpers/builder/scrollToNextStep";
  import BuilderFloorplans from "@/components/builder/BuilderFloorplans.vue";
  import BuilderSelection from "@/components/builder/BuilderSelection.vue";
  import BuilderButtonsPagination from "@/components/builder/BuilderButtonsPagination.vue";
  import { SelectionStepsIndexEnum } from "@/enums/BuilderStepsIndex";
  import { BuilderStepsIds } from "@/enums/BuilderStepsIds";
  import BuilderSliderButtons from "@/components/builder/BuilderSliderButtons.vue";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import StepperLineDesktop from "@/components/ui/StepperLineDesktop.vue";
  import SuccessBuilderQuoteModal from "@/components/ui/modals/SuccessBuilderQuoteModal.vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import StageList from "@/components/StageList.vue";
  import { MapPages } from "@/enums/MapPages";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import PhoneNumberVerificationModal from "@/components/ui/modals/PhoneNumberVerificationModal.vue";
  import { CreateBuilderQuote } from "@/types/CreateBuilderQuote";
  import { CreateInitialHoldInterface } from "@/types/InitialHoldInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderView',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const loading = ref(false);
  const isOpenedInitialHoldModal = ref(false);
  const isOpenedInitialHoldModalWithPartner = ref(false);
  const successfulInitialRequestOpened = ref(false);
  const isRequestQuotesFormOpened = ref(false);
  const isOpenBuilderQuotesListsModal = ref(false);
  const isOpenSuccessBuilderQuoteModal = ref(false);
  const isOpenPhoneNumberVerificationModal = ref(false);
  const isStageListOpen = ref(false);
  const fadeInEffect = ref(false);
  const isButtonStepHidden = ref(false);
  const isButtonStepHiddenConfirmation = ref(false);
  const isFeaturesHidden = ref(false);
  const isChangeSize = ref(false);
  const contentBackgroundColor = ref("rgb(255, 255, 255)");

  const estate = ref<EstateInterface | null>(null);
  const lot = ref<LotActionInterface>();
  const builders = ref<BuilderInterfaceWithHouseDesigns[] | null>(null);
  const selectedBuilder = ref<BuilderInterfaceWithHouseDesigns | null>();
  const selectedHouseDesign = ref<HouseDesignInterface | null>();
  const lotForBuilder = ref<LotMapInterface>();
  const quoteForVerificationPhoneNumber = ref({});
  const hasToSkipStep2 = computed(() => builders.value?.length === 1);
  const stepsNumbers = computed(() => {
    let numbers = [2, 3, 4, 5];

    if (hasToSkipStep2.value) {
      numbers = numbers.map((x) => x - 1);
    }

    return numbers;
  });

  const { isDesktop } = useIsDesktop(990);
  const lotForModal = computed(() => ({
    lotId: lot.value,
  }));
  const estateId = computed(() => {
    return router.currentRoute.value.params.estateId;
  });
  const selectedQuote = computed(
    () => store.getters["builder/getSelectedSelection"]
  );
  const currentStepIndex = computed(() => {
    const currentStep = selectedQuote.value?.currentStep;

    return Object.values(CustomizeHouseStepsEnum).indexOf(currentStep);
  });

  const currentStepValue = computed(() => {
    const currentIndex = currentStepIndex.value;

    return Object.values(CustomizeHouseStepsEnum)[currentIndex];
  });

  const nextStepValue = computed(() => {
    let nextIndex =
      (currentStepIndex.value + 1) %
      Object.values(CustomizeHouseStepsEnum).length;

    if (currentStepIndex.value === 0 && hasToSkipStep2.value) {
      nextIndex++;
    }

    return Object.values(CustomizeHouseStepsEnum)[nextIndex];
  });

  const lotIdComputed = computed(() => router.currentRoute.value.params.lotId);
  const LotId = reactive({
    value: lotIdComputed.value,
  });

  watch(
    () => LotId.value,
    (newValue) => {
      LotId.value = newValue;
    }
  );

  const lotHasBuilders = computed(() => builders.value?.length);
  const onRequestQuotes = () => (isOpenBuilderQuotesListsModal.value = true);
  const onFeaturesHidden = () => {
    isFeaturesHidden.value = true;
  };
  const onFeaturesVisibly = () => {
    isFeaturesHidden.value = false;
  };
  const onButtonStepVisibly = () => {
    isButtonStepHidden.value = false;
  };
  const onButtonStepVisiblyConfirmation = () => {
    isButtonStepHiddenConfirmation.value = false;
  };

  const onButtonStepVisiblyConfirmationOff = () => {
    isButtonStepHiddenConfirmation.value = true;
  };

  const isHouseIdNotEmpty = computed(() => {
    return selectedQuote.value.house.id !== "";
  });

  const isBuilderIdNotEmpty = computed(() => {
    return selectedQuote.value.builder.id !== "";
  });

  const isFacadeIdNotEmpty = computed(() => {
    return selectedQuote.value.facade.url !== "";
  });

  const openStageList = () => {
    isStageListOpen.value = !isStageListOpen.value;
    isChangeSize.value = true;

    setTimeout(() => {
      fadeInEffect.value = !fadeInEffect.value;
    }, 100);
  };

  const isBuilderCompleted = computed(() => {
    const builder = selectedQuote.value.builder;

    return Object.values(builder).every((field) => field !== "");
  });

  const isHouseDesignCompleted = computed(() => {
    const house = selectedQuote.value.house;

    return Object.values(house).every((field) => field !== "");
  });

  const isFacadeCompleted = computed(() => {
    const facade = selectedQuote.value.facade;

    return Object.values(facade).every((field) => field !== "");
  });

  const isNextStepEnabled = computed(() => {
    switch (currentStepValue.value) {
      case CustomizeHouseStepsEnum.BUILDER:
        return isBuilderCompleted.value;
      case CustomizeHouseStepsEnum.HOUSE_DESIGN:
        return isHouseDesignCompleted.value;
      case CustomizeHouseStepsEnum.EXTERIOR:
        return isFacadeCompleted.value;
      default:
        return true;
    }
  });

  const goToNextStep = () => {
    store.commit("builder/updateUserSelection", {
      ...selectedQuote.value,
      currentStep: nextStepValue.value,
    });
  };

  const setDefaultValues = (builder: BuilderInterfaceWithHouseDesigns) => {
    selectedBuilder.value = builder;
    updateUserSelection(CustomizeHouseStepsEnum.BUILDER);
  };
  const updateUserSelection = (step: CustomizeHouseStepsEnum, params = {}) => {
    const lotWithValue = lot.value as unknown as {
      title: string;
      price: number;
    };

    store.commit("builder/updateUserSelection", {
      ...userSelectionDefault,
      currentStep: step,
      ...params,
      id: selectedQuote.value.id,
      lot: {
        lotId: lot.value?._id,
        name: lotWithValue.title,
        price: lotWithValue.price,
      },
      builder: {
        id: selectedBuilder.value?._id,
        name: selectedBuilder.value?.name,
        logo: selectedBuilder.value?.logo,
      },
      house: {
        id: selectedHouseDesign.value?._id || "",
        name: selectedHouseDesign.value?.name || "",
        price: selectedHouseDesign.value?.basePrice || 0,
      },
    });
  };

  const clearBuilderAndHouse = () => {
    selectedBuilder.value = null;
    selectedHouseDesign.value = null;
  };
  const clearBuilderStore = () => {
    store.commit("builder/clearUserSelection");
  };
  const setUserSelectionFromBuilderStore = (
    buildersWithHouses: BuilderInterfaceWithHouseDesigns[] | null
  ): void => {
    if (!buildersWithHouses?.length) {
      return;
    }

    const { userSelectionDesign } = store.state.builder;

    if (!userSelectionDesign.length) {
      clearBuilderAndHouse();
      return;
    }

    const activeSelection = userSelectionDesign.find((item) => item.isSelected);

    if (!activeSelection?.builder.id) {
      clearBuilderAndHouse();
      return;
    }

    const { builder } = activeSelection;

    selectedBuilder.value = buildersWithHouses.find(
      (item) => item._id === builder.id
    );
    selectedHouseDesign.value = selectedBuilder.value?.houses.find(
      (item) => item._id === activeSelection.house.id
    );
  };
  const onReserveLot = () => {
    if (estate.value?.showInitialHold) {
      isOpenedInitialHoldModal.value = true;
      return;
    }

    if (!lot.value) {
      return;
    }

    const estateId = lot.value?.estateId;
    const stageId = lot.value?.stageId;
    const route = router.resolve({
      name: "EOIRequest",
      params: {
        estateId,
        lotId: lot.value?._id,
      },
      query: {
        stageId,
      },
    });

    window.open(route.href);
  };

  const onSelectBuilder = (builder: BuilderInterfaceWithHouseDesigns) => {
    clearBuilderAndHouse();
    setDefaultValues(builder);
  };

  const onSelectLot = (selectedLot: LotMapInterface) => {
    LotId.value = selectedLot.lotId._id;

    if (LotId.value) {
      router.replace({
        params: { ...router.currentRoute.value.params, lotId: LotId.value },
      });
    }

    getLotAndBuilderWithHouses();
    openStageList();
  };

  const getLotAndBuilderWithHouses = async () => {
    loading.value = true;

    try {
      const { data } = await store.dispatch(
        "builder/getBuildersByLotId",
        LotId.value
      );

      redirectIfBuilderIsDisabled(data);
      builders.value = data.builders.filter(
        (el: BuilderInterfaceWithHouseDesigns) => el.houses.length > 0
      );
      lot.value = data;
      setUserSelectionFromBuilderStore(builders.value);
      getEstateAndSetPrimaryColor(data.estateId);
      setDefaultBuilder();
    } catch (error) {
      loading.value = false;
      toast.error("Error ocurred, please try again", {
        position: POSITION.TOP_LEFT,
      });
    }
  };
  const getLotMapForBuilder = async () => {
    try {
      const { data } = await store.dispatch(
        "lot/getLotMapForBuilder",
        LotId.value
      );

      lotForBuilder.value = data;
    } catch (error) {
      toast.error("Error ocurred, please try again", {
        position: POSITION.TOP_LEFT,
      });
    }
  };

  const setDefaultBuilder = () => {
    if (builders.value && builders.value.length === 1) {
      onSelectBuilder(builders.value[0]);
      updateUserSelection(CustomizeHouseStepsEnum.LOT);
    }
  };

  const skipStep = computed(() => {
    if (hasToSkipStep2.value) {
      return CustomizeHouseStepsEnum.BUILDER;
    }

    return undefined;
  });

  const skipStepIndex = computed(() => {
    if (hasToSkipStep2.value) {
      return 1;
    }

    return undefined;
  });

  const onCloseInitialHoldModal = () => {
    isOpenedInitialHoldModal.value = false;
  };
  const onOpenPartnerModal = () => {
    isOpenedInitialHoldModal.value = false;
    isOpenedInitialHoldModalWithPartner.value = true;
  };
  const backToSelectHoldType = () => {
    isOpenedInitialHoldModal.value = true;
    isOpenedInitialHoldModalWithPartner.value = false;
  };
  const onSendHoldRequest = () => {
    isOpenedInitialHoldModal.value = false;
    successfulInitialRequestOpened.value = true;
  };
  const openRequestQuotesForm = () => {
    isOpenBuilderQuotesListsModal.value = false;
    nextTick(() => (isRequestQuotesFormOpened.value = true));
  };
  const onChangeSize = () => {
    isChangeSize.value = !isChangeSize.value;
  };
  const checkStoreHasValues = () =>
    store.getters["builder/hasUserSelectionDesignEmptyValues"];
  const submit = async () => {
    if (checkStoreHasValues()) {
      toast.error(
        "Please ensure that all selections are filled in your quotes",
        {
          position: POSITION.TOP_LEFT,
        }
      );
      isOpenBuilderQuotesListsModal.value = false;
    } else {
      isOpenBuilderQuotesListsModal.value = true;
    }
  };
  const onReturnMap = () => {
    router.push({ name: "EstateMap", params: { estateId: estateId.value } });
  };
  const openRequestQuotesFormModal = async () => {
    await submit();
  };

  const onSendRequestQuote = () => {
    isOpenSuccessBuilderQuoteModal.value = true;
    isRequestQuotesFormOpened.value = false;
    isOpenPhoneNumberVerificationModal.value = false;
  };

  const onOpenPhoneVerificationModal = (body) => {
    quoteForVerificationPhoneNumber.value = body;
    isOpenPhoneNumberVerificationModal.value = true;
    isRequestQuotesFormOpened.value = false;
  };

  const onClosePhoneVerificationModal = () => {
    isOpenPhoneNumberVerificationModal.value = false;
    isRequestQuotesFormOpened.value = true;
  };

  const onAddQuote = () => {
    if (!builders.value) {
      return;
    }

    clearBuilderAndHouse();
    changeQuoteAnimation();
    store.commit("builder/addUserSelection");
  };
  const changeQuoteAnimation = () => {
    isOpenBuilderQuotesListsModal.value = false;
    scrollToNextStepMobile(BuilderStepsIds.PREVIEW_BUILDER);
    setTimeout(() => {
      selectedQuote.value.currentStep = CustomizeHouseStepsEnum.LOT;
    }, 1000);
  };
  const onUpdateQuote = (selectionId: number) => {
    const { userSelectionDesign } = store.state.builder;

    const currentSelection = userSelectionDesign.find(
      (item) => item.id == selectionId
    );

    if (!currentSelection) {
      return;
    }

    store.commit("builder/setActiveSelection", currentSelection);
    setUserSelectionFromBuilderStore(builders.value);

    const lotId = currentSelection.lot.lotId;

    if (lotId) {
      LotId.value = lotId;
      router.replace({
        params: { ...router.currentRoute.value.params, lotId },
      });
    }

    getLotAndBuilderWithHouses();
    changeQuoteAnimation();
  };

  const onUpdateQuoteBuilder = () => {
    store.commit("builder/updateUserSelection", {
      ...selectedQuote.value,
      currentStep: CustomizeHouseStepsEnum.BUILDER,
    });
    scrollToNextStepMobile(BuilderStepsIds.BUILDER);
  };

  const onUpdateQuoteDesignAndFloorplan = () => {
    store.commit("builder/updateUserSelection", {
      ...selectedQuote.value,
      currentStep: CustomizeHouseStepsEnum.HOUSE_DESIGN,
    });
    scrollToNextStepMobile(BuilderStepsIds.HOUSE);
  };

  const onUpdateQuoteFacade = () => {
    store.commit("builder/updateUserSelection", {
      ...selectedQuote.value,
      currentStep: CustomizeHouseStepsEnum.EXTERIOR,
    });
    scrollToNextStepMobile(BuilderStepsIds.FACADE);
  };

  const onDeleteQuote = (selectionId: number) => {
    const { userSelectionDesign } = store.state.builder;

    if (selectionId === selectedQuote.value.id) {
      const nextSelection = userSelectionDesign.find(
        (item) => !item.isSelected
      );

      store.commit("builder/setActiveSelection", nextSelection);
      setUserSelectionFromBuilderStore(builders.value);
    }

    store.commit("builder/deleteSelection", selectionId);
    changeQuoteAnimation();
  };

  const getEstateAndSetPrimaryColor = (estateId: string) => {
    loading.value = true;
    store
      .dispatch("estate/getEstate", estateId)
      .then(({ data }) => {
        changePrimaryColor(data.iconColor);
        estate.value = data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  onMounted(() => {
    clearBuilderStore();
    getLotAndBuilderWithHouses();
    getLotMapForBuilder();
  });

  watch(
    selectedQuote,
    () => {
      if (!selectedQuote.value) {
        return;
      }

      const newHouseDesign = selectedBuilder.value?.houses.find(
        (item) => item._id === selectedQuote?.value.house.id
      );

      selectedHouseDesign.value = newHouseDesign;
    },
    {
      immediate: true,
      deep: true,
    }
  );

  const handleScrollButtonTransparent = () => {
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;

    if (scrollTop > 0) {
      contentBackgroundColor.value = "rgba(255, 255, 255, 0.85)";
    } else {
      contentBackgroundColor.value = "rgb(255, 255, 255)";
    }
  };

  onMounted(() => {
    window.addEventListener("scroll", handleScrollButtonTransparent);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("scroll", handleScrollButtonTransparent);
  });

const __returned__ = { toast, loading, isOpenedInitialHoldModal, isOpenedInitialHoldModalWithPartner, successfulInitialRequestOpened, isRequestQuotesFormOpened, isOpenBuilderQuotesListsModal, isOpenSuccessBuilderQuoteModal, isOpenPhoneNumberVerificationModal, isStageListOpen, fadeInEffect, isButtonStepHidden, isButtonStepHiddenConfirmation, isFeaturesHidden, isChangeSize, contentBackgroundColor, estate, lot, builders, selectedBuilder, selectedHouseDesign, lotForBuilder, quoteForVerificationPhoneNumber, hasToSkipStep2, stepsNumbers, isDesktop, lotForModal, estateId, selectedQuote, currentStepIndex, currentStepValue, nextStepValue, lotIdComputed, LotId, lotHasBuilders, onRequestQuotes, onFeaturesHidden, onFeaturesVisibly, onButtonStepVisibly, onButtonStepVisiblyConfirmation, onButtonStepVisiblyConfirmationOff, isHouseIdNotEmpty, isBuilderIdNotEmpty, isFacadeIdNotEmpty, openStageList, isBuilderCompleted, isHouseDesignCompleted, isFacadeCompleted, isNextStepEnabled, goToNextStep, setDefaultValues, updateUserSelection, clearBuilderAndHouse, clearBuilderStore, setUserSelectionFromBuilderStore, onReserveLot, onSelectBuilder, onSelectLot, getLotAndBuilderWithHouses, getLotMapForBuilder, setDefaultBuilder, skipStep, skipStepIndex, onCloseInitialHoldModal, onOpenPartnerModal, backToSelectHoldType, onSendHoldRequest, openRequestQuotesForm, onChangeSize, checkStoreHasValues, submit, onReturnMap, openRequestQuotesFormModal, onSendRequestQuote, onOpenPhoneVerificationModal, onClosePhoneVerificationModal, onAddQuote, changeQuoteAnimation, onUpdateQuote, onUpdateQuoteBuilder, onUpdateQuoteDesignAndFloorplan, onUpdateQuoteFacade, onDeleteQuote, getEstateAndSetPrimaryColor, handleScrollButtonTransparent, get router() { return router }, get store() { return store }, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, computed, ref, onMounted, onBeforeUnmount, watch, nextTick, reactive, get CustomizeHouseStepsEnum() { return CustomizeHouseStepsEnum }, get UserQuoteSelectionInterface() { return UserQuoteSelectionInterface }, get LotActionInterface() { return LotActionInterface }, get HouseDesignInterface() { return HouseDesignInterface }, get useToast() { return useToast }, get POSITION() { return POSITION }, InitialHoldModal, InitialHoldWithPartnerModal, SuccessInitialHoldModal, BuilderRequestQuoteModal, get redirectIfBuilderIsDisabled() { return redirectIfBuilderIsDisabled }, get changePrimaryColor() { return changePrimaryColor }, BuilderHeader, get EstateInterface() { return EstateInterface }, BuilderQuotesListsModal, BuilderQuotesLists, BuilderSelectionHouse, get userSelectionDefault() { return userSelectionDefault }, BuilderSelectionFacades, get scrollToNextStep() { return scrollToNextStep }, get scrollToNextStepMobile() { return scrollToNextStepMobile }, BuilderFloorplans, BuilderSelection, BuilderButtonsPagination, get SelectionStepsIndexEnum() { return SelectionStepsIndexEnum }, get BuilderStepsIds() { return BuilderStepsIds }, BuilderSliderButtons, get useIsDesktop() { return useIsDesktop }, StepperLineDesktop, SuccessBuilderQuoteModal, DropDown, StageList, get MapPages() { return MapPages }, get LotMapInterface() { return LotMapInterface }, PhoneNumberVerificationModal, get CreateBuilderQuote() { return CreateBuilderQuote }, get CreateInitialHoldInterface() { return CreateInitialHoldInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})